import React from 'react';
import { Link } from 'react-router-dom';
import { VERSION } from '../version';
import { clearLicenseAccepted } from '../app/LicenseGate';
import vduraLogo from '../assets/vdura-quote-logo.png';

/**
 * The page a signed-in user sees when no hardware configurator is enabled for
 * their account — no vendor group (aicHardware / dellHardware / smcHardware)
 * in their Cognito groups. Deliberately near-empty: nothing here is
 * confidential, so it sits OUTSIDE the license wall. Once an admin grants a
 * vendor group, their next sign-in lands in that configurator instead.
 *
 * Prop-driven and shell-agnostic: ConfiguratorGate renders it for /, /classic
 * and /v2 alike. Header markup mirrors the classic shell's top bar so the page
 * reads as the same application. Branding is the plain VDURA wordmark — the
 * combined VDURA+vendor logos would name hardware the user cannot see.
 */
export function ConfiguratorHome({ userName, showAdminLink, onSignOut }) {
  const title = process.env.REACT_APP_TITLE || 'V5000 System Configurator';

  const handleSignOut = () => {
    // Same as the classic shell and the V2 nameplate: the next person on this
    // browser must see the license wall again.
    clearLicenseAccepted();
    onSignOut?.();
  };

  return (
    <div className="space-y-8 p-6 bg-black min-h-screen text-white">
      <div className="flex-row-between">
        <div className="flex-row-spaced">
          <img src={vduraLogo} alt="VDURA" className="w-48" />
          <div className="text-white text-xl font-bold">
            {title}
            <span className="text-xs text-gray-400 ml-2">v{VERSION}</span>
          </div>
        </div>
        <div className="flex-row-spaced">
          <h1>Welcome, {userName}</h1>
          {showAdminLink && (
            <Link
              to="/admin"
              className="border-2 border-orange-400 text-orange-400 px-4 py-2 mx-2 rounded hover:bg-orange-400 hover:text-black transition duration-300"
            >
              Admin
            </Link>
          )}
          <button
            type="button"
            onClick={handleSignOut}
            className="border-2 border-white text-white px-4 py-2 rounded hover:bg-white hover:text-black transition duration-300"
          >
            Sign Out
          </button>
        </div>
      </div>

      <div className="max-w-xl mx-auto mt-24 border-2 border-orange-500 rounded-lg p-8 text-center">
        <h2 className="text-2xl font-bold text-orange-400 mb-4">No configurators enabled</h2>
        <p className="text-gray-300">
          No hardware configurators are enabled for this account yet. Contact your
          VDURA administrator to request access.
        </p>
      </div>
    </div>
  );
}

export default ConfiguratorHome;
